import React, { useState } from 'react';
import { CardBody, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import { FormikProps } from 'formik';
import SeaTrialSpeedConsumption from './SeaTrialSpeedConsumption';
import PopUp from 'Components/PopUp';
import SaveBeforeChange from 'VesselMaster/SaveBeforeChange';
import MainEngineSpeedAndConsumption from './MainEngine/MainEngineSpeedAndConsumption';
import { hasRole } from 'utils/auth/authUtils';
import { OnboardingStatus, Roles } from 'shared/constants';
import { useSelector } from 'react-redux';
import { RootState } from 'index';
import { getTabStatus } from 'GenericForms/Helper';

interface SpeedConsumptionType {
    VesselConfActiveTab: number,
    setCheckValuesBeforeSwitch?: (value: boolean) => void;
    setTabName?: (value: string) => void;
    setFormik?: (value: FormikProps<any>) => void;
    formik?: FormikProps<any>;
    deletePopOverActive?: boolean;
    setDeletePopOverActive?: (value: boolean) => void;
    checkValuesBeforeSwitch: boolean;
    subTabsStatus: any;
    saveBeforeChangePopUp: boolean;
    setDisableYesButton?: (value: boolean) => void;
}

const SpeedAndConsumption = ({
    VesselConfActiveTab,
    deletePopOverActive,
    setDeletePopOverActive,
    setTabName,
    setFormik,
    formik,
    setDisableYesButton,
    setCheckValuesBeforeSwitch,
    checkValuesBeforeSwitch,
    subTabsStatus,
    saveBeforeChangePopUp
}: SpeedConsumptionType) => {
    const [activeSettingsTab, setActiveSettingsTab] = useState(3);
    const [saveBeforeChangePopUpInsideTab, setSaveBeforeChangePopUpInsideTab] = useState(false);
    const [vesselDataTabName, setVesselDataTabName] = useState("");
    const [nextTab, setNextTab] = useState<number | null>(null);
    const [disableYesButtonInsideTab, setDisableYesButtonInsideTab] = useState(false);
    const [refreshForm, setRefreshForm] = useState(false);
    const [SNCTab, setSNCTab] = useState(3);
    const { VesselID, Vessels } = useSelector((state: RootState) => state.Reporting);
    const vessel = Vessels.find((rec: { [key: string]: string | number | boolean | Date | null }) => rec.id === VesselID);

    const toggle = (tab: number) => {
        setSNCTab(tab);
        const isSameTab = activeSettingsTab === tab;
        const isManagerWithOnboardedVessel =
            hasRole(Roles.MANAGER) && vessel.onboarding_status !== OnboardingStatus.PENDING;
        if (checkValuesBeforeSwitch) {
            if (!isSameTab) {
                setActiveSettingsTab(tab);
            }
        } else {
            setNextTab(tab);
            if (isManagerWithOnboardedVessel) {
                setActiveSettingsTab(tab);
            } else {
                setSaveBeforeChangePopUpInsideTab(true);
            }
        }
        // Force refresh the form regardless of path
        setRefreshForm(prev => !prev);
    };

    const handleNoSaveAndSwitchTab = (formik: any) => {
        formik?.resetForm({ values: formik.initialValues });
        setCheckValuesBeforeSwitch(true);
        if (nextTab !== null) {
            setActiveSettingsTab(nextTab);
            setNextTab(null);
        }
        setSaveBeforeChangePopUpInsideTab(false);
    };

    const handleSaveAndSwitchTab = async (formik: any) => {
        if (formik) {
            await formik?.submitForm();
            formik.initialValues = formik.values;
            formik?.resetForm({ values: formik.initialValues });
        }
        setCheckValuesBeforeSwitch(true);
        if (nextTab !== null) {
            setActiveSettingsTab(nextTab);
            setNextTab(null);
        }
        setSaveBeforeChangePopUpInsideTab(false);
    };

    const renderStatusIcon = (status: string) => {
        if (status === 'completed') {
            return <i className="bx bx-check-double text-success me-2" />;
        } else if (status === 'pending') {
            return <i className="mdi mdi-clock-edit-outline text-warning me-2" />;
        } else {
            return null;
        }
    };

    return (
        <React.Fragment>
            <PopUp
                state={saveBeforeChangePopUpInsideTab}
                setState={setSaveBeforeChangePopUpInsideTab}
                body={
                    <SaveBeforeChange
                        tabName={vesselDataTabName}
                        onYes={() => handleSaveAndSwitchTab(formik)} // Save and switch
                        onNo={() => {
                            handleNoSaveAndSwitchTab(formik);
                            setDisableYesButtonInsideTab(false);
                        }} // Discard and switch
                        disableYesButton={disableYesButtonInsideTab}
                    />
                }
                title="Alert"
                size="sm"
            />
            <CardBody className='pb-0 pt-0'>
                <Nav tabs className='nav-tabs-custom nav-justified'>
                    <NavItem>
                        <NavLink
                            style={{ cursor: 'pointer' }}
                            className={classnames({ active: activeSettingsTab === 3 })}
                            onClick={() => toggle(3)}
                        >{renderStatusIcon(getTabStatus('Main Engine Speed & Consumption', subTabsStatus))}
                            Main Engine Speed & Consumption
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            style={{ cursor: 'pointer' }}
                            className={classnames({ active: activeSettingsTab === 4 })}
                            onClick={() => toggle(4)}
                        >{renderStatusIcon(getTabStatus('Sea Trial Value', subTabsStatus))}
                            Sea Trial Value
                        </NavLink>
                    </NavItem>
                </Nav>
            </CardBody>

            <TabContent activeTab={activeSettingsTab} className='p-3 text-muted'>
                <TabPane tabId={3} className='m-0'>
                    <MainEngineSpeedAndConsumption
                        VesselConfActiveTab={VesselConfActiveTab}
                        activeSettingsTab={activeSettingsTab}
                        deletePopOverActive={deletePopOverActive}
                        setDeletePopOverActive={setDeletePopOverActive}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        setTabFormik={setFormik}
                        setVesselDataTabName={setVesselDataTabName}
                        saveBeforeChangePopUpInsideTab={saveBeforeChangePopUpInsideTab}
                        checkValuesBeforeSwitch={checkValuesBeforeSwitch}
                        refreshForm={refreshForm}
                        SNCTab={SNCTab}
                        subtab={subTabsStatus}
                    />
                </TabPane>
                <TabPane tabId={4} className='m-0'>
                    <SeaTrialSpeedConsumption
                        VesselConfActiveTab={VesselConfActiveTab}
                        activeSettingsTab={activeSettingsTab}
                        deletePopOverActive={deletePopOverActive}
                        setDeletePopOverActive={setDeletePopOverActive}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        setVesselDataTabName={setVesselDataTabName}
                        saveBeforeChangePopUpInsideTab={saveBeforeChangePopUpInsideTab}
                        refreshForm={refreshForm}
                        saveBeforeChangePopUp={saveBeforeChangePopUp}
                        setDisableYesButton={setDisableYesButton}
                    />
                </TabPane>
            </TabContent>
        </React.Fragment>
    );
};

export default SpeedAndConsumption;